import React, { useState, useEffect, useRef } from "react"
import { getRefId, getPersonName, getRolesText, getPersonLocation } from "../utils/utils"
import Tip from "./Tip"
import { toast } from "react-toastify"
import RoundAvatar from "./RoundAvatar"
import Card from "./Card"
import InviteToMission from "../comp/InviteToMission"
import { useVerifyOpen, verifyOpenMissionInvite } from "../utils/emailVerify"
import { Scrollbars } from "react-custom-scrollbars"
import cx from "classnames"
import Flag from "react-flagkit"
import "./Hive.scss"
import { motion, AnimatePresence } from "framer-motion"
import { PiTriangleDuotone, PiArrowArcLeftLight, PiCopy } from "react-icons/pi"
import { TbLocationPlus } from "react-icons/tb"
import PersonAdder from "./PersonAdder"
import uniqBy from "lodash/uniqBy"
import { getCustomRoleLabelByCustomRole } from "../comp/MissionUtils"

const Hive = ({
    people,
    orgData,
    mission,
    width = 30,
    onClick,
    maxLength,
    app,
    noFlip,
    add,
    onAfterInvite,
    style,
    onAdd,
    adderMsg,
    ai,
    onRemove,
    searchList,
    offset,
    showPointer,
    round,
    adderCardStyle = {},
    adderPortal,
    appendTo,
    adderPortalCenter,
    inviteMsg,
    onAddOpened,
    onAddClosed,
    pointerStyle = {},
    noTip,
    hasMax,
    // Opens mAX on what the hive is about (a task's own conversation). Without
    // it the mAX avatar is only a picture, greyed out.
    onMaxClick,
}) => {
    const peopleToUse = uniqBy(
        (people || []).map((p) => ({ ...p, rf: getRefId(p) })),
        "rf",
    )

    let map = [...peopleToUse.slice(0, maxLength || people.length)]

    if (hasMax) {
        map.push({
            email: "help@missionx.ai",
            ref: {
                firstName: "mAX",
                lastName: "AI",
                avatar: "/img/mAX.png",
                email: "help@missionx.ai",
                _id: "isMAX",
            },
        })
    }

    const [inviting, setInviting] = useState()
    const [adding, setAdding] = useState()

    // Back from the verification email the invite card sent (the first hive of the mission opens it)
    useVerifyOpen(add && mission?._id ? verifyOpenMissionInvite(mission._id) : null, () => setInviting(true))

    const meInMission = mission?.people?.find((p) => getRefId(p) === app.state.person._id)
    const meInOrg = orgData?.people?.find((p) => getRefId(p) === app.state.person._id)

    if (maxLength && maxLength < peopleToUse.length) {
        map.push({
            plus: peopleToUse.length - maxLength,
            _id: "plus" + (mission._id || orgData._id),
        })
    }
    if (add) {
        map.push({
            add: true,
            _id: "add" + (mission._id || orgData._id),
        })
    }

    useEffect(() => {
        if (adding && onAddOpened) {
            onAddOpened()
        } else if (onAddClosed && !adding) {
            onAddClosed()
        }
    }, [adding, onAddOpened, onAddClosed])

    return (
        <>
            <ul className="x-hive" onClick={onClick} style={style}>
                <AnimatePresence>
                    {map.map((obj, index) => {
                        return (
                            <Person
                                key={getRefId(obj)}
                                plus={obj.plus}
                                add={obj.add}
                                person={obj}
                                orgData={orgData}
                                mission={mission}
                                width={width}
                                index={index}
                                people={people}
                                meInMission={meInMission}
                                meInOrg={meInOrg}
                                maxLength={maxLength}
                                app={app}
                                onAdd={onAdd}
                                setInviting={setInviting}
                                setAdding={setAdding}
                                onRemove={onRemove}
                                offset={offset}
                                ai={ai}
                                round={round}
                                inviteMsg={inviteMsg}
                                noTip={noTip}
                                onMaxClick={onMaxClick}
                            />
                        )
                    })}
                </AnimatePresence>
            </ul>

            <AnimatePresence>
                {showPointer && people.length <= 1 && (
                    <motion.div
                        style={{ pointerEvents: "none", position: "absolute", left: 70, top: 20, ...pointerStyle }}
                        initial={{
                            opacity: 0,
                            x: 20,
                        }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{
                            opacity: 0,
                            x: 20,
                        }}
                        transition={{ duration: 1, delay: 1, type: "spring", bounce: 0.6 }}
                    >
                        <PiArrowArcLeftLight /> Invite people
                    </motion.div>
                )}
            </AnimatePresence>

            <Card closeButton modal modalIndex={9997} portal center onClose={() => setInviting(false)} open={inviting}>
                <Scrollbars className="dna-invite-scroll" autoHeight={true} autoHeightMin={400} autoHeightMax={"97vh"}>
                    {inviting && (
                        <InviteToMission
                            app={app}
                            missionData={mission}
                            afterInvite={(res) => {
                                if (onAfterInvite) onAfterInvite(res)
                            }}
                            inviteToMission={() => {
                                setInviting(false)
                            }}
                            onCancel={() => {
                                setInviting(false)
                            }}
                        />
                    )}
                </Scrollbars>
            </Card>

            <Card
                padded
                style={{ paddingTop: 0, width: 400, ...adderCardStyle }}
                className="dna-add-people-card x-ai-adder-style"
                onClose={() => setAdding(false)}
                open={adding}
                autoClose={true}
                portal={adderPortal}
                appendTo={appendTo}
                center={adderPortalCenter}
            >
                {adding && (
                    <PersonAdder
                        org={orgData}
                        mission={mission}
                        people={people}
                        searchList={searchList || mission?.people.filter((p) => p.permission < 3)}
                        onAdd={onAdd}
                        app={app}
                        adderMsg={adderMsg}
                        startInvite={() => {
                            setAdding(false)
                            setInviting(true)
                        }}
                    />
                )}
            </Card>
        </>
    )
}

// A diamond with rounded corners as a clip-path, so the avatar stays upright and
// only the outline is a diamond. d = the diamond's diagonal, r = corner rounding.
const diamondPath = (d, r) => {
    const h = d / 2
    const k = r / Math.SQRT2
    return (
        `path("M ${h - k} ${k} Q ${h} 0 ${h + k} ${k} L ${d - k} ${h - k} Q ${d} ${h} ${d - k} ${h + k} ` +
        `L ${h + k} ${d - k} Q ${h} ${d} ${h - k} ${d - k} L ${k} ${h + k} Q 0 ${h} ${k} ${h - k} Z")`
    )
}

const Person = ({
    person,
    orgData,
    mission,
    width,
    index,
    plus,
    add,
    people,
    app,
    meInOrg,
    meInMission,
    maxLength,
    setInviting,
    setAdding,
    onAdd,
    onRemove,
    offset,
    ai,
    round,
    roundSpace = 2,
    inviteMsg,
    noTip,
    onMaxClick,
}) => {
    const isMax = getRefId(person) === "isMAX"
    // Stagger only the entrance; hover and un-hover must never wait.
    const entered = useRef(false)
    useEffect(() => {
        entered.current = true
    }, [])
    const enterDelay = entered.current ? 0 : 0.25 * index
    const odd = index % 2 === 1

    // A fresh membership keeps its ref as a plain id until the person is snapshotted:
    // the organisation's record carries the avatar then.
    const avatar = person?.ref?.avatar || orgData?.people?.find((p) => getRefId(p) === getRefId(person))?.ref?.avatar

    const notSeen = !isMax && person?.email && ai && !ai?.seen?.find((o) => o.by === getRefId(person))

    // Diamonds sit on a zigzag: each one half a diagonal to the right of the last,
    // odd ones half a diagonal lower, so their facing edges line up with a small gap.
    const size = round ? width : Math.round(width * Math.SQRT2)
    const step = size / 2 + Math.SQRT1_2 * 5

    const liStyle = {
        width: size,
        height: size,
        marginLeft: index === 0 ? 0 : round ? roundSpace : step - size,
        marginTop: !round && odd ? step : 0,
    }

    const faceStyle = add
        ? { width, height: width }
        : {
              width: size,
              height: size,
              backgroundImage: plus ? undefined : `url(${avatar || "/img/avatar-blur-red.jpg"})`,
              clipPath: round ? undefined : diamondPath(size, width * 0.25),
              borderRadius: round ? "50%" : undefined,
          }

    const onFaceClick = isMax
        ? onMaxClick &&
          ((e) => {
              e.stopPropagation()
              onMaxClick()
          })
        : add &&
          ((e) => {
              e.stopPropagation()
              e.preventDefault()
              if (onAdd) setAdding(true)
              else setInviting(true)
          })

    const details = (
        <Details
            person={person}
            meInOrg={meInOrg}
            meInMission={meInMission}
            mission={mission}
            orgData={orgData}
            app={app}
            onRemove={onRemove}
            ai={ai}
            canAskMax={true}
        />
    )

    const tip = isMax ? (
        details
    ) : noTip ? undefined : add ? (
        inviteMsg || "Invite..."
    ) : plus ? (
        <Plus
            people={people}
            meInOrg={meInOrg}
            meInMission={meInMission}
            mission={mission}
            orgData={orgData}
            maxLength={maxLength}
            app={app}
            ai={ai}
        />
    ) : (
        details
    )

    return (
        <motion.li
            className={cx({
                "x-hive-max": isMax,
                "x-hive-max-idle": isMax && !onMaxClick,
                "x-hive-plus-count": plus,
                "x-hive-adder": add,
                "x-hive-not-seen": notSeen,
                pending: person?.invitePending,
            })}
            style={liStyle}
            initial={{ opacity: 0, scale: 0.2 }}
            animate={{
                opacity: notSeen ? 0.7 : 1,
                scale: 1,
                rotate: isMax ? -2 : 0,
                transition: { type: "spring", stiffness: 500, damping: 25, delay: enterDelay },
            }}
            exit={{ opacity: 0, scale: 0.2, transition: { duration: 0.2, delay: 0.25 * index } }}
            whileHover={{
                opacity: 1,
                scale: isMax ? 1.4 : 1.2,
                rotate: isMax ? 2 : 0,
                transition: { type: "spring", stiffness: 500, damping: 25, delay: 0 },
            }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
        >
            <Tip
                interactive={true}
                interactiveBorder={50}
                distance={isMax ? 20 : 5}
                delay={400}
                noFlip={true}
                offset={offset}
                position={plus ? "right" : odd ? "bottom" : "top"}
                html={tip}
            >
                {isMax ? (
                    <img
                        className="x-hive-div"
                        src="/img/mAX.png"
                        style={{ width: width + 8, cursor: onMaxClick ? "pointer" : undefined }}
                        onClick={onFaceClick || undefined}
                    />
                ) : (
                    <div className="x-hive-div" style={faceStyle} onClick={onFaceClick || undefined}>
                        {plus && `+${plus}`}
                        {add && <TbLocationPlus style={{ fontSize: width / 1.7 }} />}
                    </div>
                )}
            </Tip>
        </motion.li>
    )
}

const Details = ({ mission, person, meInOrg, meInMission, orgData, app, onRemove, ai, noTip, canAskMax }) => {
    const personInOrg = orgData?.people?.find((p) => getRefId(p) === getRefId(person))
    const personInMission = mission?.people?.find((p) => getRefId(p) === getRefId(person))

    const isMax = getRefId(person) === "isMAX"

    // A fresh membership keeps its ref as a plain id until the person is snapshotted:
    // borrow the organisation's ref (name + avatar) then, keeping the membership's own fields.
    const personToUse = getRefId(personInMission)
        ? personInMission.ref?.avatar || !personInOrg?.ref?.avatar
            ? personInMission
            : { ...personInMission, ref: personInOrg.ref }
        : personInOrg || person

    const canAdmin =
        meInOrg?.permission === 2 ||
        meInMission?.permission === 2 ||
        (meInMission?.permission === 1 && meInMission?.isProjectManager)

    const myOrgRole = personInOrg
        ? orgData.roles?.find((r) => {
              return r._id === personInOrg.role
          })
        : null
    const myDepartment = personInOrg?.departments?.length
        ? orgData.departments?.find((r) => {
              return r._id === personInOrg.departments[0]
          })
        : null

    const myOffice = personInOrg
        ? orgData.offices?.find((r) => {
              return r._id === personInOrg.office
          })
        : null

    const missionRoles = mission ? getRolesText({ mission, person: personToUse, orgData }) : []

    return (
        <div className="x-hive-details">
            <RoundAvatar
                warning={personToUse.invitePending}
                person={personToUse}
                noTip={true}
                width={68}
                style={{ marginRight: 20 }}
            />
            <div>
                <h3>{getPersonName(personToUse)}</h3>

                {myOrgRole?.name && <p>{myOrgRole?.name}</p>}
                <div className="dna-flex">
                    <p
                        className="no-wrap x-link"
                        style={{ marginBottom: 10 }}
                        onClick={() => {
                            window.location.href = "mailto:" + personToUse?.email
                        }}
                    >
                        {personToUse?.email}{" "}
                    </p>
                    <span
                        style={{ flexShrink: 0, marginLeft: 5, marginTop: 1, fontSize: 16, cursor: "pointer" }}
                        onClick={() => {
                            navigator.clipboard
                                .writeText(personToUse?.email)
                                .then(() => toast("Copied", { autoClose: 1000 }))
                        }}
                    >
                        <PiCopy />
                    </span>
                </div>

                {isMax && (
                    <>
                        <p>
                            {true
                                ? "Click to ask mAX about this task. Everyone on the task sees the conversation."
                                : "Your friendly AI assistant. Ask mAX for help with this activity."}
                        </p>
                    </>
                )}

                {personInMission && mission && (
                    <>
                        <p style={{ marginBottom: 5 }}>
                            <b>
                                {personInMission.customRole?.length > 0
                                    ? person.customRole.map((c) => getCustomRoleLabelByCustomRole(c)).join(", ")
                                    : personInMission.isProjectManager
                                      ? "Project manager"
                                      : personInMission.permission === 2
                                        ? "Admin/Owner"
                                        : personInMission.permission === 1
                                          ? "Team lead"
                                          : personInMission.permission === 0
                                            ? "Team member"
                                            : "Observer"}
                            </b>
                        </p>
                        <p style={{ marginBottom: 10 }} className="no-wrap">
                            Project roles:{" "}
                            {missionRoles?.length ? (
                                missionRoles.join(", ")
                            ) : (
                                <>
                                    <PiTriangleDuotone color={"orange"} /> Not assigned any roles
                                </>
                            )}
                        </p>
                    </>
                )}

                {personInMission?.invitePending && (
                    <p style={{ marginBottom: 15 }} className="x-inset-light orange-bg">
                        FYI: They have not accepted the mission invite yet.
                    </p>
                )}

                {personInOrg && (
                    <div style={{ opacity: 0.7 }}>
                        <p>
                            Department: {myDepartment?.title || "Not assigned"}{" "}
                            <>
                                {myOffice ? (
                                    <>
                                        <span className="x-flag" style={{ marginLeft: 6, marginRight: 3 }}>
                                            <Flag country={myOffice.country} width={15} />
                                        </span>{" "}
                                        {myOffice.title}
                                    </>
                                ) : (
                                    <>&nbsp; {getPersonLocation(personInMission)}</>
                                )}
                            </>
                        </p>
                    </div>
                )}

                {((mission && canAdmin) || (ai && onRemove)) && !isMax && (
                    <span
                        style={{ marginTop: 10, display: "inline-block" }}
                        className="x-link caps gold small"
                        onClick={() => {
                            if (onRemove) {
                                onRemove(getRefId(person))
                            } else {
                                if (getRefId(person) === app.state.person._id) {
                                    app.confirm({
                                        severe: true,
                                        comp: (
                                            <p>
                                                Are you sure you want to remove yourself from the mission? If you are
                                                the only admin or pm, the project can only be accessed from the
                                                organization workspace.
                                            </p>
                                        ),
                                        onYes: () => {
                                            app.missionDeletePerson({
                                                person: person,
                                                noToast: true,
                                                mission: mission,
                                                app,
                                            })
                                        },
                                    })
                                } else {
                                    app.missionDeletePerson({
                                        person: person,
                                        noToast: true,
                                        mission: mission,
                                        app,
                                    })
                                }
                            }
                        }}
                    >
                        Remove this person
                    </span>
                )}
            </div>
        </div>
    )
}

const Plus = ({
    people,
    maxLength,
    orgData,
    meInOrg,
    meInMission,
    personInMission,
    personInOrg,
    mission,
    noFlip,
    app,
    ai,
    noTip,
}) => {
    return (
        <div className="x-hive-plus">
            {people.slice(maxLength, people.length).map((person) => {
                return (
                    <Details
                        person={person}
                        meInOrg={meInOrg}
                        meInMission={meInMission}
                        personInMission={personInMission}
                        personInOrg={personInOrg}
                        mission={mission}
                        orgData={orgData}
                        noFlip={noFlip}
                        maxLength={maxLength}
                        app={app}
                        ai={ai}
                        noTip={noTip}
                    />
                )
            })}
        </div>
    )
}

export default Hive
